// src/pages/.../ManageMembers/ManageAdherentGroupsModal.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  addAdherentToGroup,
  deleteAdherentFromGroup,
} from "../../../services/adherentServices";

import { getGroupsByClub } from "../../../services/groupServices";

import { useNotification } from "../../../components/Notification/NotificationManager";

import {
  FaCheck,
  FaLayerGroup,
  FaSave,
  FaSearch,
  FaSpinner,
  FaTimes,
  FaUser,
} from "react-icons/fa";

import {
  cn,
  getGroupDojoId,
  getGroupDojoName,
} from "./utils/manageMembers.utils";

const ManageAdherentGroupsModal = ({
  isOpen,
  onClose,
  adherent,
  adherentGroups = [],
  clubId,
  onUpdate,
  demoMode = false,
}) => {
  const { addNotification } = useNotification();

  const [allGroups, setAllGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  /*
   * Groupes présents à l'ouverture de la modale.
   * Ils servent à calculer les ajouts et suppressions au moment
   * de l'enregistrement.
   */
  const [initialSelectedGroupIds, setInitialSelectedGroupIds] = useState(
    () => new Set(),
  );

  /*
   * Sélection actuellement visible dans la modale.
   * Le clic sur une case ne fait que modifier ce Set local.
   */
  const [selectedGroupIds, setSelectedGroupIds] = useState(
    () => new Set(),
  );

  const fetchGroups = useCallback(
    async (currentClubId) => {
      if (!currentClubId) {
        setAllGroups([]);
        return;
      }

      setLoading(true);

      try {
        const data = await getGroupsByClub(currentClubId);

        setAllGroups(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error(
          "Erreur lors du chargement des groupes :",
          error,
        );

        setAllGroups([]);

        addNotification(
          "Erreur lors du chargement des groupes.",
          "error",
        );
      } finally {
        setLoading(false);
      }
    },
    [addNotification],
  );

  useEffect(() => {
    if (!isOpen) return;

    const currentGroupIds = new Set(
      (Array.isArray(adherentGroups) ? adherentGroups : [])
        .map((group) => String(group?.id))
        .filter(Boolean),
    );

    setInitialSelectedGroupIds(new Set(currentGroupIds));
    setSelectedGroupIds(new Set(currentGroupIds));

    setSearchTerm("");
    setSaving(false);

    fetchGroups(clubId);
  }, [
    isOpen,
    adherentGroups,
    clubId,
    fetchGroups,
  ]);

  const filteredGroups = useMemo(() => {
    const normalizedSearch = searchTerm
      .trim()
      .toLowerCase();

    return [...allGroups]
      .filter((group) => {
        if (!normalizedSearch) return true;

        const searchableContent = [
          group?.nom_groupe,
          getGroupDojoName(group),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return searchableContent.includes(normalizedSearch);
      })
      .sort((first, second) => {
        const firstDojo = getGroupDojoName(first);
        const secondDojo = getGroupDojoName(second);

        const dojoComparison = firstDojo.localeCompare(
          secondDojo,
          "fr",
          {
            sensitivity: "base",
          },
        );

        if (dojoComparison !== 0) {
          return dojoComparison;
        }

        return String(first?.nom_groupe || "").localeCompare(
          String(second?.nom_groupe || ""),
          "fr",
          {
            sensitivity: "base",
          },
        );
      });
  }, [allGroups, searchTerm]);

  const groupedByDojo = useMemo(() => {
    const dojoMap = new Map();

    filteredGroups.forEach((group) => {
      const dojoId =
        getGroupDojoId(group) ?? "unknown-dojo";

      const dojoKey = String(dojoId);
      const dojoName = getGroupDojoName(group);

      if (!dojoMap.has(dojoKey)) {
        dojoMap.set(dojoKey, {
          id: dojoKey,
          name: dojoName,
          groups: [],
        });
      }

      dojoMap.get(dojoKey).groups.push(group);
    });

    return [...dojoMap.values()];
  }, [filteredGroups]);

  const isDirty = useMemo(() => {
    if (
      selectedGroupIds.size !==
      initialSelectedGroupIds.size
    ) {
      return true;
    }

    for (const groupId of selectedGroupIds) {
      if (!initialSelectedGroupIds.has(groupId)) {
        return true;
      }
    }

    return false;
  }, [
    selectedGroupIds,
    initialSelectedGroupIds,
  ]);

  const addedGroupIds = useMemo(() => {
    return [...selectedGroupIds].filter(
      (groupId) =>
        !initialSelectedGroupIds.has(groupId),
    );
  }, [
    selectedGroupIds,
    initialSelectedGroupIds,
  ]);

  const removedGroupIds = useMemo(() => {
    return [...initialSelectedGroupIds].filter(
      (groupId) => !selectedGroupIds.has(groupId),
    );
  }, [
    selectedGroupIds,
    initialSelectedGroupIds,
  ]);

  const handleToggleGroup = (groupId) => {
    if (demoMode) {
      addNotification(
        "Mode Démo : gestion désactivée.",
        "warning",
      );

      return;
    }

    if (saving) return;

    const normalizedGroupId = String(groupId);

    setSelectedGroupIds((previousIds) => {
      const nextIds = new Set(previousIds);

      if (nextIds.has(normalizedGroupId)) {
        nextIds.delete(normalizedGroupId);
      } else {
        nextIds.add(normalizedGroupId);
      }

      return nextIds;
    });
  };

  const handleSave = async () => {
    if (demoMode) {
      addNotification(
        "Mode Démo : gestion désactivée.",
        "warning",
      );

      return;
    }

    if (!adherent?.id) {
      addNotification(
        "L'adhérent est introuvable.",
        "error",
      );

      return;
    }

    if (selectedGroupIds.size === 0) {
      addNotification(
        "L'adhérent doit appartenir à au moins un groupe.",
        "error",
      );

      return;
    }

    if (!isDirty) {
      addNotification(
        "Aucune modification à enregistrer.",
        "info",
      );

      onClose();
      return;
    }

    setSaving(true);

    try {
      const addRequests = addedGroupIds.map((groupId) =>
        addAdherentToGroup(Number(groupId), {
          adherentId: adherent.id,
        }),
      );

      const removeRequests = removedGroupIds.map((groupId) =>
        deleteAdherentFromGroup(
          adherent.id,
          Number(groupId),
        ),
      );

      await Promise.all([
        ...addRequests,
        ...removeRequests,
      ]);

      /*
       * On synchronise les identifiants initiaux avec la nouvelle
       * sélection pour éviter que la modale reste marquée comme
       * modifiée.
       */
      setInitialSelectedGroupIds(
        new Set(selectedGroupIds),
      );

      if (typeof onUpdate === "function") {
        await onUpdate();
      }

      const messages = [];

      if (addedGroupIds.length > 0) {
        messages.push(
          `${addedGroupIds.length} groupe${
            addedGroupIds.length > 1 ? "s" : ""
          } ajouté${
            addedGroupIds.length > 1 ? "s" : ""
          }`,
        );
      }

      if (removedGroupIds.length > 0) {
        messages.push(
          `${removedGroupIds.length} groupe${
            removedGroupIds.length > 1 ? "s" : ""
          } retiré${
            removedGroupIds.length > 1 ? "s" : ""
          }`,
        );
      }

      addNotification(
        messages.length > 0
          ? `Affectations enregistrées : ${messages.join(
              " et ",
            )}.`
          : "Affectations enregistrées.",
        "success",
      );

      onClose();
    } catch (error) {
      console.error(
        "Erreur lors de l'enregistrement des groupes :",
        error,
      );

      addNotification(
        "Erreur lors de l'enregistrement des groupes.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !adherent) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[220] bg-slate-950/75 backdrop-blur-sm">
      <div className="flex h-[100dvh] items-stretch justify-center p-0 sm:p-4">
        <div className="flex h-[100dvh] w-full max-w-6xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900 sm:h-auto sm:max-h-[calc(100dvh-2rem)] sm:rounded-[28px] sm:border sm:border-white/10 dark:sm:border-slate-700">
          {/* Header */}
          <div className="shrink-0 border-b border-slate-200 bg-white px-4 py-4 dark:border-slate-800 dark:bg-slate-900 sm:px-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wider text-purple-600 dark:text-purple-400">
                  Affectation aux groupes
                </p>

                <h2 className="mt-1 text-xl font-black text-slate-900 dark:text-slate-100 sm:text-2xl">
                  Groupes de l’adhérent
                </h2>

                <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                  Cochez les groupes souhaités puis cliquez sur
                  Enregistrer.
                </p>
              </div>

              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className={cn(
                  "inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-600 transition dark:border-slate-700 dark:text-slate-300",
                  saving
                    ? "cursor-not-allowed opacity-50"
                    : "hover:bg-slate-50 dark:hover:bg-slate-800",
                )}
                aria-label="Fermer"
              >
                <FaTimes />
              </button>
            </div>
          </div>

          {/* Zone scrollable */}
          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="grid min-h-full grid-cols-1 lg:grid-cols-[340px_minmax(0,1fr)]">
              {/* Colonne adhérent */}
              <aside className="border-b border-white/10 bg-gradient-to-br from-slate-900 via-slate-800 to-purple-900 p-5 text-white lg:border-b-0 lg:border-r lg:border-white/10 sm:p-6">
                <p className="inline-flex rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
                  Adhérent
                </p>

                <div className="mt-6 flex flex-col items-center rounded-[24px] border border-white/10 bg-white/10 p-5 text-center">
                  {adherent.photo_url ? (
                    <img
                      src={adherent.photo_url}
                      alt={`${adherent.prenom} ${adherent.nom}`}
                      className="h-24 w-24 rounded-full object-cover shadow-xl"
                    />
                  ) : (
                    <div className="flex h-24 w-24 items-center justify-center rounded-full bg-white/10 text-white/70">
                      <FaUser className="text-4xl" />
                    </div>
                  )}

                  <h3 className="mt-4 text-xl font-bold sm:text-2xl">
                    {adherent.prenom} {adherent.nom}
                  </h3>

                  <p className="mt-2 text-sm text-white/75">
                    {selectedGroupIds.size} groupe
                    {selectedGroupIds.size > 1 ? "s" : ""}{" "}
                    sélectionné
                    {selectedGroupIds.size > 1 ? "s" : ""}
                  </p>

                  <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-2 text-xs font-semibold text-white/90">
                    <FaLayerGroup />
                    Validation manuelle
                  </div>
                </div>

                <div className="mt-5 rounded-2xl border border-white/10 bg-white/10 p-4">
                  <p className="text-sm font-semibold">
                    Fonctionnement
                  </p>

                  <p className="mt-2 text-sm leading-6 text-white/75">
                    Vous pouvez cocher ou décocher plusieurs
                    groupes. Les modifications ne seront envoyées
                    qu’au clic sur Enregistrer.
                  </p>
                </div>

                {isDirty && (
                  <div className="mt-5 rounded-2xl border border-amber-300/20 bg-amber-400/10 p-4">
                    <p className="text-sm font-bold text-amber-200">
                      Modifications en attente
                    </p>

                    <div className="mt-2 space-y-1 text-sm text-white/75">
                      {addedGroupIds.length > 0 && (
                        <p>
                          + {addedGroupIds.length} groupe
                          {addedGroupIds.length > 1
                            ? "s"
                            : ""}{" "}
                          à ajouter
                        </p>
                      )}

                      {removedGroupIds.length > 0 && (
                        <p>
                          − {removedGroupIds.length} groupe
                          {removedGroupIds.length > 1
                            ? "s"
                            : ""}{" "}
                          à retirer
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </aside>

              {/* Colonne groupes */}
              <main className="p-4 sm:p-6">
                <div className="relative">
                  <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(event) =>
                      setSearchTerm(event.target.value)
                    }
                    disabled={saving}
                    placeholder="Rechercher un groupe ou un lieu..."
                    className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 outline-none transition focus:border-purple-500 focus:ring-4 focus:ring-purple-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-purple-400 dark:focus:ring-purple-500/20"
                  />
                </div>

                <div className="mt-5 flex items-center justify-between gap-4">
                  <div>
                    <p className="font-bold text-slate-900 dark:text-slate-100">
                      Groupes disponibles
                    </p>

                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                      {allGroups.length} groupe
                      {allGroups.length > 1 ? "s" : ""} dans
                      le club
                    </p>
                  </div>

                  <span
                    className={cn(
                      "inline-flex rounded-full px-3 py-1.5 text-xs font-bold",
                      isDirty
                        ? "bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300"
                        : "bg-purple-50 text-purple-700 dark:bg-purple-500/10 dark:text-purple-300",
                    )}
                  >
                    {isDirty
                      ? "Non enregistré"
                      : "À jour"}
                  </span>
                </div>

                {loading ? (
                  <div className="mt-10 flex flex-col items-center justify-center text-slate-500 dark:text-slate-400">
                    <FaSpinner className="animate-spin text-3xl text-purple-600" />

                    <p className="mt-3 text-sm">
                      Chargement des groupes...
                    </p>
                  </div>
                ) : groupedByDojo.length > 0 ? (
                  <div className="mt-6 space-y-7">
                    {groupedByDojo.map((dojoSection) => (
                      <section key={dojoSection.id}>
                        <div className="mb-3 flex items-center gap-3">
                          <div className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />

                          <p className="shrink-0 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                            {dojoSection.name}
                          </p>

                          <div className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />
                        </div>

                        <div className="grid gap-3 sm:grid-cols-2">
                          {dojoSection.groups.map((group) => {
                            const groupId = String(group.id);

                            const isSelected =
                              selectedGroupIds.has(groupId);

                            const wasInitiallySelected =
                              initialSelectedGroupIds.has(
                                groupId,
                              );

                            const hasChanged =
                              isSelected !==
                              wasInitiallySelected;

                            return (
                              <button
                                key={group.id}
                                type="button"
                                role="checkbox"
                                aria-checked={isSelected}
                                disabled={saving || demoMode}
                                onClick={() =>
                                  handleToggleGroup(group.id)
                                }
                                className={cn(
                                  "group flex min-h-[92px] w-full items-center gap-4 rounded-2xl border p-4 text-left transition",
                                  isSelected
                                    ? "border-purple-500 bg-purple-50 shadow-sm ring-2 ring-purple-100 dark:border-purple-400 dark:bg-purple-500/10 dark:ring-purple-500/10"
                                    : "border-slate-200 bg-white hover:border-purple-300 hover:bg-purple-50/40 dark:border-slate-700 dark:bg-slate-800/50 dark:hover:border-purple-500 dark:hover:bg-purple-500/5",
                                  hasChanged &&
                                    "border-amber-400 ring-2 ring-amber-100 dark:border-amber-400 dark:ring-amber-500/10",
                                  (saving || demoMode) &&
                                    "cursor-not-allowed opacity-70",
                                )}
                              >
                                <span
                                  className={cn(
                                    "flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border-2 transition",
                                    isSelected
                                      ? "border-purple-600 bg-purple-600 text-white dark:border-purple-400 dark:bg-purple-500"
                                      : "border-slate-300 bg-white text-transparent group-hover:border-purple-400 dark:border-slate-600 dark:bg-slate-900",
                                  )}
                                >
                                  <FaCheck className="text-xs" />
                                </span>

                                <span className="min-w-0 flex-1">
                                  <span className="block break-words font-bold text-slate-900 dark:text-slate-100">
                                    {group.nom_groupe}
                                  </span>

                                  <span className="mt-1 block text-sm text-slate-500 dark:text-slate-400">
                                    {getGroupDojoName(group)}
                                  </span>
                                </span>

                                <span
                                  className={cn(
                                    "shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold",
                                    hasChanged
                                      ? "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300"
                                      : isSelected
                                        ? "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-300"
                                        : "bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300",
                                  )}
                                >
                                  {hasChanged
                                    ? isSelected
                                      ? "À ajouter"
                                      : "À retirer"
                                    : isSelected
                                      ? "Affecté"
                                      : "Non affecté"}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </section>
                    ))}
                  </div>
                ) : (
                  <div className="mt-8 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center dark:border-slate-700 dark:bg-slate-800/40">
                    <FaLayerGroup className="mx-auto text-3xl text-slate-400" />

                    <p className="mt-3 font-semibold text-slate-700 dark:text-slate-200">
                      Aucun groupe trouvé
                    </p>

                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                      Vérifiez la recherche ou créez un groupe
                      dans la gestion du club.
                    </p>
                  </div>
                )}
              </main>
            </div>
          </div>

          {/* Footer */}
          <div className="shrink-0 border-t border-slate-200 bg-white px-4 py-4 dark:border-slate-800 dark:bg-slate-900 sm:px-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {isDirty
                  ? "Des modifications sont en attente d’enregistrement."
                  : "Les affectations sont à jour."}
              </p>

              <div className="flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={saving}
                  className={cn(
                    "inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-5 py-3 font-semibold text-slate-800 transition dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100",
                    saving
                      ? "cursor-not-allowed opacity-50"
                      : "hover:bg-slate-50 dark:hover:bg-slate-700",
                  )}
                >
                  <FaTimes />
                  Fermer
                </button>

                <button
                  type="button"
                  onClick={handleSave}
                  disabled={
                    saving ||
                    demoMode ||
                    !isDirty ||
                    selectedGroupIds.size === 0
                  }
                  className={cn(
                    "inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3 font-semibold text-white transition",
                    saving ||
                      demoMode ||
                      !isDirty ||
                      selectedGroupIds.size === 0
                      ? "cursor-not-allowed bg-slate-400 dark:bg-slate-700"
                      : "bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600",
                  )}
                >
                  {saving ? (
                    <FaSpinner className="animate-spin" />
                  ) : (
                    <FaSave />
                  )}

                  {saving
                    ? "Enregistrement..."
                    : "Enregistrer"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageAdherentGroupsModal;